import React, { useEffect, useState } from "react";
import { Box, Card, CardContent, Typography, Avatar } from "@mui/material";
import { styled } from "@mui/system";
import { FaMale, FaFemale } from "react-icons/fa";
import { motion } from "framer-motion";
import bgImage from "./messiah.png"; // Import your local background image

// Styled Components
const BackgroundSection = styled(Box)({
  width: "100%",
  minHeight: "100vh",
  backgroundImage: `url(${bgImage})`,
  backgroundSize: "cover",
  backgroundPosition: "center",
  display: "flex",
  justifyContent: "center",
  alignItems: "center",
  position: "relative",
  padding: "2rem",
  overflow: "hidden",
  "&::before": {
    content: '""',
    position: "absolute",
    top: 0,
    left: 0,
    width: "100%",
    height: "100%",
    backgroundColor: "rgba(0,0,0,0.5)", // dim overlay
    zIndex: 1,
  },
});

const ContentWrapper = styled(Box)(({ theme }) => ({
  display: "flex",
  justifyContent: "space-between",
  width: "100%",
  maxWidth: "1200px",
  gap: "2rem",
  position: "relative",
  zIndex: 2,
  [theme.breakpoints.down("md")]: {
    flexDirection: "column",
  },
}));

const StatsCard = styled(Card)({
  flex: 1,
  backgroundColor: "rgba(255,255,255,0.2)",
  color: "#fff",
  backdropFilter: "blur(10px)",
  borderRadius: "15px",
});

const StatItem = styled(Box)({
  display: "flex",
  alignItems: "center",
  marginBottom: "1rem",
  "& svg": {
    fontSize: "2rem",
    marginRight: "0.5rem",
  },
});

const AnnouncementsContainer = styled(Box)(({ theme }) => ({
  flex: 2,
  maxHeight: "400px",
  overflowY: "auto",
  padding: "1rem",
  borderRadius: "15px",
  backgroundColor: "rgba(255,255,255,0.1)",
  color: "#fff",
  "&::-webkit-scrollbar": {
    width: "8px",
  },
  "&::-webkit-scrollbar-thumb": {
    backgroundColor: "rgba(255,255,255,0.3)",
    borderRadius: "4px",
  },
  [theme.breakpoints.down("md")]: {
    maxHeight: "auto",
  },
}));

const AnnouncementItem = styled(motion.div)({
  padding: "1rem",
  marginBottom: "1rem",
  borderRadius: "10px",
  backgroundColor: "rgba(1, 50, 139, 0.15)",
});

const SchoolAnnouncement = () => {
  const [stats, setStats] = useState({
    maleStudents: 120,
    femaleStudents: 110,
    maleStaff: 20,
    femaleStaff: 25,
  });

  const [announcements, setAnnouncements] = useState([
    {
      title: "School Reopening",
      description: "School will reopen on 5th January 2026 for all students.",
      createdBy: "Admin",
    },
    {
      title: "New Library Books",
      description: "We have added 200 new books to the library collection.",
      createdBy: "Librarian",
    },
    {
      title: "Sports Day",
      description: "Annual Sports Day will be held on 20th February 2026.",
      createdBy: "Sports Coordinator",
    },
    {
      title: "Parent-Teacher Meeting",
      description: "Next PTM will be on 10th January 2026. Attendance is compulsory.",
      createdBy: "Principal",
    },
  ]);

  return (
    <BackgroundSection
      as={motion.div}
      initial={{ opacity: 0, y: 50 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 1 }}
    >
      <ContentWrapper>
        {/* Left Stats Card */}
        <StatsCard component={motion.div} 
          initial={{ x: -50, opacity: 0 }} 
          animate={{ x: 0, opacity: 1 }} 
          transition={{ duration: 1 }}
        >
          <CardContent>
            <Typography variant="h5" gutterBottom>
              School Statistics
            </Typography>

            <StatItem>
              <FaMale />
              <Typography>Male Students: {stats.maleStudents}</Typography>
            </StatItem>
            <StatItem>
              <FaFemale />
              <Typography>Female Students: {stats.femaleStudents}</Typography>
            </StatItem>
            <StatItem>
              <FaMale />
              <Typography>Male Staff: {stats.maleStaff}</Typography>
            </StatItem>
            <StatItem>
              <FaFemale />
              <Typography>Female Staff: {stats.femaleStaff}</Typography>
            </StatItem>
          </CardContent>
        </StatsCard>

        {/* Right Announcement Section */}
        <AnnouncementsContainer
          as={motion.div}
          initial={{ x: 50, opacity: 0 }}
          animate={{ x: 0, opacity: 1 }}
          transition={{ duration: 1 }}
        >
          <Typography variant="h5" gutterBottom>
            School Announcements
          </Typography>

          {announcements.map((item, index) => (
            <AnnouncementItem
              key={index}
              whileHover={{ scale: 1.02 }}
              whileInView={{ opacity: 1, y: 0 }}
              initial={{ opacity: 0, y: 20 }}
              transition={{ duration: 0.5, delay: index * 0.1 }}
            >
              <Typography variant="h6">{item.title}</Typography>
              <Typography variant="body2">{item.description}</Typography>
              <Typography variant="caption">Posted by: {item.createdBy}</Typography>
            </AnnouncementItem>
          ))}
        </AnnouncementsContainer>
      </ContentWrapper>
    </BackgroundSection>
  );
};

export default SchoolAnnouncement;
